<template>
  <ZtTooltip v-if="collapsed" content="Help" placement="right" :offset-size="10">
    <ZtPopup ref="popup" teleport placement="right-end" :offset-size="10" @open:popup="onOpen" @close:popup="onClose">
      <button
        ref="trigger"
        type="button"
        class="help-menu-trigger help-menu-trigger--collapsed"
        :class="{ 'help-menu-trigger--open': isOpen }"
        aria-label="Help"
        aria-haspopup="menu"
        :aria-expanded="isOpen"
        data-testid="viewer-help-menu-trigger"
        @keydown.down.prevent="openMenu(0)"
        @keydown.up.prevent="openMenu(helpMenuLinks.length - 1)"
      >
        <Icon name="general.help" :size="22" />
      </button>

      <template #content>
        <HelpMenuPanel @activate="onLinkActivated" @keydown="onMenuKeydown" />
      </template>
    </ZtPopup>
  </ZtTooltip>

  <ZtPopup v-else ref="popup" teleport placement="right-end" :offset-size="10" @open:popup="onOpen" @close:popup="onClose">
    <button
      ref="trigger"
      type="button"
      class="help-menu-trigger"
      :class="{ 'help-menu-trigger--open': isOpen }"
      aria-haspopup="menu"
      :aria-expanded="isOpen"
      data-testid="viewer-help-menu-trigger"
      @keydown.down.prevent="openMenu(0)"
      @keydown.up.prevent="openMenu(helpMenuLinks.length - 1)"
    >
      <Icon name="general.help" :size="22" />
      <span>Help</span>
      <Icon name="general.chevronRight" size="sm" class="help-menu-trigger__chevron" />
    </button>

    <template #content>
      <HelpMenuPanel @activate="onLinkActivated" @keydown="onMenuKeydown" />
    </template>
  </ZtPopup>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { Icon } from '@/storybook/icon';
import { ZtPopup } from '@/storybook/popup';
import { ZtTooltip } from '@/storybook/tooltip';
import { TrackEvents, useTrack } from '@/composables/useTrack';
import { helpMenuLinks, type HelpMenuLink } from './helpMenuLinks';
import HelpMenuPanel from './HelpMenuPanel.vue';

const props = defineProps<{ collapsed: boolean }>();
const emit = defineEmits<{ open: []; close: [] }>();

const route = useRoute();
const { track } = useTrack();
const popup = ref<InstanceType<typeof ZtPopup> | null>(null);
const trigger = ref<HTMLButtonElement | null>(null);
const isOpen = ref(false);
const focusIndexAfterOpen = ref<number | null>(null);
const returnFocusAfterClose = ref(false);

const railMode = computed(() => (props.collapsed ? 'compact' : 'expanded'));

const focusMenuItem = (index: number | null) => {
  if (index === null) {
    return;
  }

  nextTick(() => {
    document.querySelectorAll<HTMLElement>('[data-testid^="viewer-help-menu-link-"]')[index]?.focus();
  });
};

const onOpen = () => {
  isOpen.value = true;
  emit('open');
  track(TrackEvents.HELP_MENU_OPENED, { surface: 'viewer', rail_mode: railMode.value });
  focusMenuItem(focusIndexAfterOpen.value);
  focusIndexAfterOpen.value = null;
};

const onClose = () => {
  isOpen.value = false;
  emit('close');
  if (returnFocusAfterClose.value) {
    returnFocusAfterClose.value = false;
    nextTick(() => trigger.value?.focus());
  }
};

const openMenu = (focusIndex: number) => {
  focusIndexAfterOpen.value = focusIndex;
  if (isOpen.value) {
    focusMenuItem(focusIndex);
    return;
  }

  popup.value?.openPopup();
};

const closeMenu = (shouldReturnFocus = false) => {
  returnFocusAfterClose.value = shouldReturnFocus;
  popup.value?.closePopup();
};

const rememberEscapeFocus = (event: KeyboardEvent) => {
  if (isOpen.value && event.key === 'Escape') {
    returnFocusAfterClose.value = true;
  }
};

onMounted(() => document.addEventListener('keydown', rememberEscapeFocus, true));
onBeforeUnmount(() => document.removeEventListener('keydown', rememberEscapeFocus, true));

const onMenuKeydown = (event: KeyboardEvent, index: number) => {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault();
    const offset = event.key === 'ArrowDown' ? 1 : -1;
    focusMenuItem((index + offset + helpMenuLinks.length) % helpMenuLinks.length);
    return;
  }

  if (event.key === 'Home' || event.key === 'End') {
    event.preventDefault();
    focusMenuItem(event.key === 'Home' ? 0 : helpMenuLinks.length - 1);
    return;
  }

  if (event.key === 'Escape') {
    event.preventDefault();
    closeMenu(true);
  }
};

const onLinkActivated = (link: HelpMenuLink) => {
  track(TrackEvents.HELP_LINK_CLICKED, { link_id: link.id, surface: 'viewer', rail_mode: railMode.value });
  closeMenu();
};

watch(
  () => route.fullPath,
  () => closeMenu()
);

defineExpose({ closeMenu });
</script>

<style scoped>
.help-menu-trigger {
  @apply flex h-10 w-full items-center gap-3.5 rounded-10 px-3.5 py-[9px] text-left text-slate-600 transition-colors dark:text-zinc-300;
  @apply hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50;
  font-family:
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 20px;
}

.help-menu-trigger--open {
  @apply bg-slate-100 text-slate-700 dark:bg-zinc-800;
}

.help-menu-trigger--collapsed {
  @apply justify-center px-0;
}

.help-menu-trigger__chevron {
  @apply ml-auto transition-transform;
}

.help-menu-trigger--open .help-menu-trigger__chevron {
  transform: rotate(180deg);
}

@media (prefers-reduced-motion: reduce) {
  .help-menu-trigger {
    transition: none;
  }
}
</style>
